<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AppDialog from '../AppDialog/AppDialog.vue'
import type { AlertDialogVariant } from './alert-dialog-types'

const open = defineModel<boolean>('open', { default: false })

const props = withDefaults(
  defineProps<{
    /** Heading - required, wired as the `aria-labelledby` target. */
    title: string
    /** Optional supporting copy, wired as the `aria-describedby` target. */
    description?: string
    confirmLabel?: string
    cancelLabel?: string
    /** Drops Cancel for a notice the user only acknowledges. */
    hideCancel?: boolean
    variant?: AlertDialogVariant
    /** Allow Escape / overlay click to dismiss. Off by default (a11y). */
    dismissible?: boolean
  }>(),
  {
    description: undefined,
    confirmLabel: undefined,
    cancelLabel: undefined,
    hideCancel: false,
    variant: 'default',
    dismissible: false,
  },
)

const emit = defineEmits<{
  confirm: []
  cancel: []
}>()

const { t } = useI18n()

const resolvedConfirmLabel = computed(() => props.confirmLabel ?? t('common.confirm'))

function onConfirm(): void {
  emit('confirm')
  open.value = false
}
</script>

<template>
  <AppDialog
    v-model:open="open"
    role="alertdialog"
    width="sm"
    :title="title"
    :description="description"
    :dismissible="dismissible"
    :confirm-label="resolvedConfirmLabel"
    :confirm-variant="variant === 'destructive' ? 'destructive' : 'primary'"
    :cancel-label="cancelLabel"
    :hide-cancel="hideCancel"
    @confirm="onConfirm"
    @cancel="emit('cancel')"
    @dismiss="emit('cancel')"
  >
    <slot v-if="$slots.default" />
  </AppDialog>
</template>
